// Fetch the workbench chunks ahead of need, without competing with the view
// that is on screen.
//
// main.js used to preload every page 3 seconds after a public route mounted:
// all the tools at once (~450 KB gzipped), whatever the visitor came to see
// and however slow their connection. Now, once the page has loaded and gone
// idle, the tool the visitor is looking at comes first (a landing view's own
// workbench), then the rest one at a time, each waiting for the previous
// download and for the next idle period. A visitor who presses "Create free
// account" gets that tool fetched at once, so it is ready when they finish
// signing up. Nothing is preloaded on a data-saver or 2G connection; the
// tool simply loads when it is opened.
//
// The specifiers match router.js, so Vite emits one chunk per tool and a
// preload here is a cache hit when the route renders it.
export const TOOL_CHUNKS = Object.freeze({
    g25: () => import('@/pages/g25.vue'),
    calculators: () => import('@/pages/admixCalc.vue'),
    admixplus: () => import('@/pages/formalstats.vue'),
    pca: () => import('@/pages/pca.vue'),
    familytree: () => import('@/pages/familytree.vue'),
    health: () => import('@/pages/health.vue'),
})

const started = new Map()

export function preloadTool(key) {
    const load = TOOL_CHUNKS[key]
    if (!load) return Promise.resolve()
    if (!started.has(key)) {
        started.set(key, load().catch(err => {
            // Let a later attempt (the route itself) try again.
            started.delete(key)
            console.log('[preloadTools]', key, err)
        }))
    }
    return started.get(key)
}

function constrainedConnection() {
    const c = typeof navigator !== 'undefined' && navigator.connection
    return !!(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || '')))
}

function whenIdle(fn) {
    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(fn, { timeout: 5000 })
    } else {
        setTimeout(fn, 200)
    }
}

// first: a TOOL_CHUNKS key to fetch before the others (the landing showing).
// extra: further loaders (the non-tool pages) to fetch after the tools.
export function preloadWhenIdle({ first = null, extra = [] } = {}) {
    if (typeof window === 'undefined' || constrainedConnection()) return
    const keys = Object.keys(TOOL_CHUNKS)
    const queue = [
        ...(first && TOOL_CHUNKS[first] ? [first] : []),
        ...keys.filter(k => k !== first),
    ].map(k => () => preloadTool(k)).concat(extra)
    const next = () => {
        const job = queue.shift()
        if (!job) return
        Promise.resolve()
            .then(job)
            .catch(() => {})
            .then(() => whenIdle(next))
    }
    const start = () => whenIdle(next)
    if (document.readyState === 'complete') start()
    else window.addEventListener('load', start, { once: true })
}
